import { Jsona } from 'jsona'
import { cloneDeep } from 'lodash-es'
import { JsonaAttributes, JsonaRelationships, JsonaModel } from './types'
import { JsonPropertiesMapper } from './JsonPropertiesMapper'
import { ModelPropertiesMapper } from './ModelPropertiesMapper'

export class AppJsona extends Jsona {
  modelPropertiesMapper!: ModelPropertiesMapper

  jsonPropertiesMapper!: JsonPropertiesMapper

  /**
   * Create new model
   */
  new<M extends JsonaModel>(type: string, attributes: JsonaAttributes = {}, relationships: JsonaRelationships = {}): M {
    return this.instance(type, null, attributes, relationships)
  }

  /**
   * Build model instance
   */
  instance<M extends JsonaModel>(
    type: string,
    id: string | null,
    attributes: JsonaAttributes = {},
    relationships: JsonaRelationships = {}
  ): M {
    const model = this.jsonPropertiesMapper.createModel(type)
    if (id) {
      this.jsonPropertiesMapper.setId(model, id)
    }

    this.jsonPropertiesMapper.setAttributes(model, attributes)
    this.jsonPropertiesMapper.setRelationships(model, <any>relationships)

    return <any>model
  }

  /**
   * Verify that provided value is jsona model
   */
  verifyModel(model: JsonaModel | JsonaModel[]) {
    if (Array.isArray(model)) {
      model.forEach(item => this.verifyModel(item))
    } else if (!model || !this.modelPropertiesMapper.getType(model)) {
      throw new Error('Object is not proper Jsona model.')
    }
  }

  /**
   * Append relation
   */
  appendRelation(model: JsonaModel, name: string, relation: JsonaModel | JsonaModel[]): JsonaModel {
    this.verifyModel(model)
    this.verifyModel(relation)

    this.jsonPropertiesMapper.setRelationships(model, { [name]: relation })

    return model
  }

  /**
   * Clone model
   */
  clone<M extends JsonaModel>(model: M, onlyAttributes?: string[] | keyof M, onlyRelationships?: string[]): M {
    this.verifyModel(model)

    const currID = this.modelPropertiesMapper.getId(model)
    const currType = this.modelPropertiesMapper.getType(model)
    const currAttributes = this.modelPropertiesMapper.getAttributes(model)
    const currRelationships: JsonaRelationships = this.modelPropertiesMapper.getRelationships(model) || {}
    const currLinks = this.modelPropertiesMapper.getLinks(model)
    const currMeta = this.modelPropertiesMapper.getMeta(model)

    const newAttributes = !Array.isArray(onlyAttributes)
      ? { ...cloneDeep(currAttributes) }
      : Object.keys(currAttributes).reduce(
        (acc, curr) => (onlyAttributes.includes(curr) ? { ...acc, [curr]: cloneDeep(currAttributes[curr]) } : acc),
        {}
      )

    const newRelationships = !Array.isArray(onlyRelationships)
      ? { ...currRelationships }
      : Object.keys(currRelationships).reduce(
        (acc, curr) => (onlyRelationships.includes(curr) ? { ...acc, [curr]: currRelationships[curr] } : acc),
        {}
      )

    const newModel = this.instance(currType, currID, newAttributes || {}, newRelationships || {})

    if (currLinks) {
      this.jsonPropertiesMapper.setLinks(newModel, currLinks)
    }

    if (currMeta) {
      this.jsonPropertiesMapper.setMeta(newModel, currMeta)
    }

    return <M>newModel
  }

  /**
   * Clone model with only specific attributes or relationships.
   */
  only<M extends JsonaModel>(model: M, onlyAttributes?: string[], onlyRelationships?: string[]): M {
    this.verifyModel(model)

    onlyAttributes = Array.isArray(onlyAttributes) ? onlyAttributes : []
    onlyRelationships = Array.isArray(onlyRelationships) ? onlyRelationships : []

    return this.clone(model, onlyAttributes, onlyRelationships)
  }
}
